import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Monitor, Smartphone, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { TrustedDeviceResponse } from 'types'

import useErrorMessage from '@hooks/use-error-message'

import { Button } from '@shadcn/button'

import {
  useMutationTrustedDeviceRevoke,
  useMutationTrustedDeviceRevokeAll,
} from '@api/trusted-device/trusted-device.mutations'
import { queryTrustedDevices } from '@api/trusted-device/trusted-device.queries'

import { cn } from '@/lib/utils'

import SectionHeader from '../../-components/section-layout/section-header'

export const Route = createFileRoute('/app/settings/trusted-devices/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const errorMessage = useErrorMessage()

  const { data: devices = [] } = useQuery(queryTrustedDevices())

  const { mutate: revokeAll, isPending: isRevokingAll } =
    useMutationTrustedDeviceRevokeAll()

  const handleRevokeAll = () => {
    revokeAll(undefined, {
      onSuccess: () =>
        toast.success(t('settings.trusted-devices.revoke-all.success')),
      onError: (error) => toast.error(errorMessage({ error })),
    })
  }

  return (
    <div className="flex h-full grow flex-col divide-y overflow-hidden">
      <SectionHeader isSecondary title={t('settings.trusted-devices.title')}>
        <div className="flex items-center gap-2">
          <Button
            variant="outlineError"
            onClick={handleRevokeAll}
            disabled={isRevokingAll}
            className={cn(!devices.length && 'invisible')}
          >
            <Trash2 className="size-4" />
            {t('settings.trusted-devices.revoke-all.label')}
          </Button>
        </div>
      </SectionHeader>

      <div className="flex justify-center">
        <div className="flex w-full max-w-2xl flex-col gap-2 p-2">
          <p className="text-text-secondary-700 text-sm">
            {t('settings.trusted-devices.description')}
          </p>

          {!devices.length ? (
            <p className="text-text-secondary-500 text-sm italic">
              {t('settings.trusted-devices.empty')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {devices.map((device) => (
                <DeviceDisplay device={device} key={`device-${device.uuid}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

interface DeviceDisplayProps {
  device: TrustedDeviceResponse
}

const DeviceDisplay = ({ device }: DeviceDisplayProps) => {
  const { t } = useTranslation()
  const errorMessage = useErrorMessage()

  const { mutate: revoke, isPending: isRevoking } =
    useMutationTrustedDeviceRevoke()

  const handleRevoke = () => {
    revoke(device.uuid, {
      onSuccess: () =>
        toast.success(t('settings.trusted-devices.revoke.success')),
      onError: (error) => toast.error(errorMessage({ error })),
    })
  }

  return (
    <div
      key={device.uuid}
      className="flex items-center justify-between rounded-md border bg-white p-4"
    >
      <div className="flex items-center gap-3">
        {device.deviceInfo?.toLowerCase().includes('mobile') ? (
          <Smartphone className="text-text-secondary-500 size-5" />
        ) : (
          <Monitor className="text-text-secondary-500 size-5" />
        )}
        <div className="flex flex-col">
          <span className="text-sm font-medium">
            {device.deviceInfo ?? t('settings.trusted-devices.unknown-device')}
            {device.isCurrent && (
              <span className="ml-2 rounded-md bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">
                {t('settings.trusted-devices.current')}
              </span>
            )}
          </span>
          <span className="text-text-secondary-500 text-xs">
            {device.ip} · {t('settings.trusted-devices.added')}{' '}
            {new Date(device.createdAt).toLocaleDateString()} ·{' '}
            {t('settings.trusted-devices.expires')}{' '}
            {new Date(device.expiresAt).toLocaleDateString()}
          </span>
        </div>
      </div>
      <Button
        variant="ghostError"
        size="ghost"
        onClick={handleRevoke}
        disabled={isRevoking}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  )
}
